import { observer } from 'mobx-react-lite';
import { titlePlaceholderText } from './TitleAndSuggestionsWithCharm.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import getComposeStyles from '../utils/getComposeStyles';
import { isCssVariablesFeatureEnabled } from 'owa-calendar-feature-flags';
import type { CharmInfo } from 'owa-calendar-charms-store';
import charmSuggestionsStore from 'owa-calendar-charm-suggestions-store/lib/store';
import { CharmControlColorPattern, CharmPickerBackground } from 'owa-charmcontrol';
import { dismissCharmSuggestions } from 'owa-calendar-charm-suggestions-store/lib/actions/charmSuggestionActions';
import { QuickComposeControl } from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import {
    logQuickComposeControlUsed,
    onCharmSuggestionChanged,
    onManualCharmChanged,
    verifyEventThenSave,
} from 'owa-calendar-compose-form-actions';
import subjectOnKeyDown from 'owa-calendar-compose-form-view/lib/utils/subjectOnKeyDown';
import SmartCharmPicker, {
    SmartCharmPickerHandle,
} from 'owa-calendar-compose-form-view/lib/components/SmartCharmPicker';
import TitleAndSuggestions from 'owa-calendar-compose-form-view/lib/components/TitleAndSuggestions';
import { newOofCalendarEvent } from '../actions/newOofCalendarEvent';
import isOofPivotActive from '../utils/isOofPivotActive';
import getQuickComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getQuickComposeViewState';

const styles = require('./TitleAndSuggestionsWithCharm.scss');
const classNames = require('classnames/bind').bind(styles);

const subjectInputID: string = 'subjectInput';

export interface TitleAndSuggestionsWithCharmProps {
    colorScheme: ColorScheme;
    isInEditMode: boolean;
    charmDisabled?: boolean;
}

export default observer(function TitleAndSuggestionsWithCharm(
    props: TitleAndSuggestionsWithCharmProps
) {
    const charmPicker = React.useRef<SmartCharmPickerHandle>();
    // This is used to fix a callout dismissal bug in IE11 with React 16 and Fabric Contextual Menus
    const charmOnItemSelectedWasCalled = React.useRef<boolean>(false);
    const onManualCharmChanged_0 = (charm: CharmInfo) => {
        onManualCharmChanged(charm);
        charmOnItemSelectedWasCalled.current = true;
    };
    /**
     * Callback issued when the menu is dimissed to ensure the focus goes back to the command button
     */
    const onAfterMenuDismissPreventIE11React16CalloutDismiss = () => {
        if (charmOnItemSelectedWasCalled.current) {
            charmPicker.current.focus();
            charmOnItemSelectedWasCalled.current = false;
        }
    };
    const { colorScheme, isInEditMode, charmDisabled } = props;
    const { viewType } = getQuickComposeViewState();
    const subjectInputIdTarget = '#' + subjectInputID;
    const composeGeneratedStyles = getComposeStyles(
        !isCssVariablesFeatureEnabled(),
        colorScheme,
        styles.useCssVariables,
        styles.editTitleTextInputCssVariables
    );
    let charmsContainerClassName, charmsSuggestionsClassName;
    if (viewType == 'TimePanel') {
        // If we are in the timepanel view, then the width of the charms callout should be different
        // since the quick compose in timepanel is narrower
        charmsContainerClassName = classNames(
            styles.charmContextMenuContainer,
            styles.timePanelCharmContextMenuContainer
        );
        charmsSuggestionsClassName = styles.timePanelCharmSugggestionsCallout;
    } else if (isInEditMode) {
        charmsContainerClassName = classNames(
            styles.charmContextMenuContainer,
            styles.editCharmContextMenuContainer
        );
        charmsSuggestionsClassName = styles.editCharmSuggestionCallout;
    } else {
        charmsContainerClassName = classNames(
            styles.charmContextMenuContainer,
            styles.defaultCharmContextMenuContainer
        );
        charmsSuggestionsClassName = styles.charmSuggestionsCallout;
    }
    return (
        <>
            <div className={styles.charmPickerCell} id={subjectInputID}>
                <SmartCharmPicker
                    ref={ref => {
                        if (ref) {
                            charmPicker.current = ref;
                        }
                    }}
                    onSelectIcon={onManualCharmChanged_0}
                    colorScheme={colorScheme}
                    buttonClassName={styles.charmIconButton}
                    charmClassName={styles.charmIconTitle}
                    resultContainerClassName={charmsContainerClassName}
                    colorPattern={CharmControlColorPattern.Normal}
                    background={CharmPickerBackground.Light}
                    onAfterMenuDismiss={onAfterMenuDismissPreventIE11React16CalloutDismiss}
                    disabled={charmDisabled}
                />
            </div>
            <TitleAndSuggestions
                inputStyle={composeGeneratedStyles.textFieldStyle}
                className={classNames(
                    styles.editTitleTextInput,
                    composeGeneratedStyles.editTitleTextStyle
                )}
                autoFocus={true}
                onKeyDown={onSubjectKeydown}
                placeholder={loc(titlePlaceholderText)}
                onSelectCharmSuggestion={onCharmSuggestionChanged}
                charmSuggestionsClassName={charmsSuggestionsClassName}
                charmSuggestionsTarget={subjectInputIdTarget}
                colorScheme={colorScheme}
                shouldUpdateSelectionRange={false}
                onFocus={onSubjectFocus}
                onBlur={onSubjectBlur}
                charmDisabled={charmDisabled}
            />
        </>
    );
});

function onSubjectFocus() {
    logQuickComposeControlUsed(QuickComposeControl.Subject, true /* isFocus*/);
}

function onSubjectBlur() {
    logQuickComposeControlUsed(QuickComposeControl.Subject, false /* isFocus*/);
}

function onSubjectKeydown(target: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (charmSuggestionsStore.shouldShowSuggestions) {
        subjectOnKeyDown(target, dismissCharmSuggestions, onCharmSuggestionChanged);
    } else if (target.keyCode == KeyCodes.enter) {
        target.preventDefault();
        if (isOofPivotActive()) {
            newOofCalendarEvent();
        } else {
            verifyEventThenSave();
        }
    }
}
